<script setup lang="ts">
    import { watch, computed, ref } from 'vue'
    import { useAuth } from '~/composables/useAuth'
    import PkChatbot from 'components/src/chat/PkChatbot.vue'
    import type { AgentPublic } from 'models'
    import { getSessionCookie } from '~/utils/cookie'
    import { getContrast } from 'components/src/chat/utils'

    const props = withDefaults(
        defineProps<{
            agentId: string
            organizationSlug: string
            prefixUrl?: string
        }>(),
        {
            prefixUrl: import.meta.env.VITE_BACKEND_URL,
        },
    )

    const auth = useAuth()
    const session = auth.useSession()

    watch(
        session,
        (newValue) => {
            if (
                !newValue.isPending &&
                !newValue.isRefetching &&
                !newValue.data
            ) {
                auth.signIn.anonymous()
            }
        },
        { immediate: true },
    )

    const api = computed(() => {
        return `${props.prefixUrl}/api/v1/public`
    })

    const headers = computed(() => ({
        Authorization: `Bearer ${getSessionCookie()}`,
    }))

    const agent = ref<AgentPublic>()

    watch(
        [
            () => props.organizationSlug,
            () => props.agentId,
            () => session.value.data,
        ],
        async ([newOrganizationSlug, newAgentId, newSession]) => {
            if (!newOrganizationSlug || !newAgentId || !newSession) {
                return
            }
            const response = await fetch(
                `${api.value}/organizations/${newOrganizationSlug}/agents/${newAgentId}`,
                {
                    credentials: 'include',
                    headers: headers.value,
                },
            )
            if (response.ok) {
                agent.value = await response.json()
            }
        },
        { immediate: true },
    )

    // chatbot ref
    const chatbotEl = ref<InstanceType<typeof PkChatbot>>()

    // open and close chat widget
    const isChatOpen = ref(false)
    const toggleChatOpen = () => {
        isChatOpen.value = !isChatOpen.value

        // stop generation when closing the chat while it's generating
        if (!isChatOpen.value) {
            chatbotEl.value?.stopGeneration()
        }
    }

    const contrastColor = computed(() =>
        getContrast(agent.value?.interface.mainColor),
    )
</script>

<template>
    <button
        v-if="
            !session.isPending && !session.isRefetching && session.data && agent
        "
        type="button"
        aria-label="chat-button"
        class="floating-chat-widget__button"
        :style="{
            '--chatbot-main-color': agent.interface.mainColor,
            '--chatbot-contrast-color': contrastColor,
        }"
        @click.stop="toggleChatOpen">
        <div class="flex items-center justify-center w-full h-full p-10">
            <svg
                v-if="isChatOpen"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor">
                <path
                    d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z"></path>
            </svg>
            <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor">
                <path
                    d="M12 1.99996C12.8632 1.99996 13.701 2.10973 14.5 2.31539L14 4.25192C13.3608 4.0874 12.6906 3.99997 12 3.99997C7.58174 3.99997 4.00002 7.58172 4 12C4 13.3344 4.3255 14.6174 4.93945 15.7656L5.28906 16.4189L4.63379 19.3662L7.58105 18.7109L8.23438 19.0605C9.38255 19.6745 10.6656 20 12 20C16.4183 20 20 16.4183 20 12C20 11.6771 19.9805 11.3587 19.9434 11.0459L21.9297 10.8095C21.976 11.1999 22 11.5972 22 12C22 17.5228 17.5228 22 12 22C10.2975 22 8.69425 21.5746 7.29102 20.8242L2 22L3.17578 16.709C2.42541 15.3057 2 13.7025 2 12C2.00002 6.47714 6.47717 1.99996 12 1.99996ZM19.5293 1.3193C19.7058 0.893513 20.2942 0.8935 20.4707 1.3193L20.7236 1.93063C21.1555 2.97343 21.9615 3.80614 22.9746 4.2568L23.6914 4.57614C24.1022 4.75882 24.1022 5.35635 23.6914 5.53903L22.9326 5.87692C21.945 6.3162 21.1534 7.11943 20.7139 8.1279L20.4668 8.69333C20.2863 9.10747 19.7136 9.10747 19.5332 8.69333L19.2861 8.1279C18.8466 7.11942 18.0551 6.3162 17.0674 5.87692L16.3076 5.53903C15.8974 5.35618 15.8974 4.75895 16.3076 4.57614L17.0254 4.2568C18.0384 3.80614 18.8445 2.97343 19.2764 1.93063L19.5293 1.3193Z"></path>
            </svg>
        </div>
    </button>
    <PkChatbot
        v-if="
            !session.isPending &&
            !session.isRefetching &&
            session.data &&
            agent &&
            isChatOpen
        "
        ref="chatbotEl"
        class="floating-chat-widget__chat"
        :api
        :agent-id
        :user-id="session.data?.user.id"
        :name="agent.name"
        :agent-interface="agent.interface"
        :headers
        :actions="['upvote', 'downvote', 'feedback', 'regenerate']">
        <template #actions="{ useColorsForAgentHeader }">
            <VvButton
                :modifiers="
                    useColorsForAgentHeader
                        ? `action-quiet-${contrastColor}`
                        : 'action-quiet'
                "
                icon="ri:close-line"
                :title="$t('action.close')"
                @click.stop="toggleChatOpen" />
        </template>
    </PkChatbot>
</template>

<style lang="scss">
    // Design system CSS (variables, base styles, typography…) injected into the shadow DOM.
    @use '~/assets/scss/style';

    // Sub-component styles (PkChatbotMessages, PkChatbotInput, etc.) are automatically
    // collected from each component's own <style> block because those files match the
    // customElement function in vite.config.ts (packages/components/src/chat/).
    // Vue's defineCustomElement then injects them into the shadow DOM — both in dev and build.

    .floating-chat-widget {
        &__button {
            position: fixed;
            border: 0px;
            bottom: 16px;
            right: 16px;
            width: 55px;
            height: 55px;
            border-radius: 27.5px;
            background-color: var(
                --chatbot-main-color,
                var(--color-black, #000)
            );
            color: var(--chatbot-contrast-color, var(--color-white, #fff));
            box-shadow: rgb(0 0 0 / 20%) 0px 4px 8px 0px;
            cursor: pointer;
            z-index: 2147483645;
            pointer-events: auto;
            transition: 0.2s ease-in-out;
            display: block;
            left: unset;
            transform: scale(1);

            &:hover {
                transform: scale(1.1);
            }
        }

        &__chat {
            z-index: var(--z-popover);
            position: fixed;
            inset: 16px 16px 85px;
            height: auto;
            border-radius: var(--rounded-xxl);
            box-shadow:
                0px 260px 73px 0px rgb(0 0 0 / 0%),
                0px 166px 67px 0px rgb(0 0 0 / 1%),
                0px 94px 56px 0px rgb(0 0 0 / 2%),
                0px 42px 42px 0px rgb(0 0 0 / 3%),
                0px 10px 23px 0px rgb(0 0 0 / 4%);
            backdrop-filter: blur(4px);
            overflow: hidden;

            @include media-breakpoint-up('sm', $breakpoints) {
                top: unset;
                right: 16px;
                left: unset;
                width: 406px;
                height: 85dvh;
            }
        }
    }
</style>
